import React from 'react';
import { CircularProgress, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Image from 'modules/views/shared/Image';
import { ReactComponent as CloseIcon } from 'assets/icons/Clear.svg';
import { ReactComponent as ExpandIcon } from 'assets/icons/ExpandIcon.svg';

import { useStyles } from '../styles';

export interface IGalleryImage {
  fileId: string;
  fileName: string;
  url: string;
  uploadedAt?: string;
}

interface IProfileGalleryModalProps {
  open: boolean;
  images: IGalleryImage[];
  totalImages: number;
  isLoading: boolean;
  onClose: () => void;
  onImageExpand: (url: string, label: string) => void;
}

const ProfileGalleryModal: React.FC<IProfileGalleryModalProps> = ({
  open,
  images,
  totalImages,
  isLoading,
  onClose,
  onImageExpand,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className={classes.galleryDialog}
      data-testid="profile-gallery-modal"
    >
      <DialogTitle className={classes.galleryDialogTitle} component="div">
        {t('myProfile.profilePicture.gallery.title')} ({totalImages} {t('common.images') || 'Images'})
        <IconButton
          onClick={onClose}
          size="small"
          data-testid="profile-gallery-close-button"
          className={classes.galleryDialogCloseButton}
        >
          <CloseIcon className={classes.galleryDialogCloseIcon} />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.galleryContent}>
        {isLoading ? (
          <div className={classes.loaderContainer} data-testid="profile-gallery-loader">
            <CircularProgress />
          </div>
        ) : images.length === 0 ? (
          <p className={classes.galleryEmptyText} data-testid="profile-gallery-empty-state">
            {t('myProfile.profilePicture.gallery.empty')}
          </p>
        ) : (
          images.map((image, index) => (
            <div key={image.fileId} className={classes.galleryItem} data-testid={`profile-gallery-item-${index}`}>
              {/* Image */}
              <Image
                src={image.url}
                alt={image.fileName}
                className={classes.galleryThumbnail}
                fallbackClassName={classes.galleryThumbnailFallback}
              />

              {/* Text */}
              <div className={classes.galleryItemInfo}>
                <p className={classes.galleryItemName}>
                  {t('myProfile.profilePicture.gallery.photo')} #{images.length - index}
                  {index === 0 && (
                    <span className={classes.galleryCurrentBadge}>{t('myProfile.profilePicture.gallery.current')}</span>
                  )}
                </p>
                <p className={classes.galleryItemMeta}>{image.uploadedAt || '-'}</p>
              </div>

              {/* Zoom icon — aligned to top-right of card */}
              <IconButton
                className={classes.galleryExpandButton}
                size="small"
                data-testid={`profile-gallery-expand-button-${index}`}
                onClick={() => onImageExpand(image.url, `${t('myProfile.profilePicture.gallery.photo')} #${images.length - index}`)}
                aria-label="Expand image"
              >
                <ExpandIcon className={classes.galleryExpandIcon} />
              </IconButton>
            </div>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileGalleryModal;
